import { useState, useCallback } from 'react';
import { FormikProps } from 'formik';
import { AddressSearchType, useAddressAutocomplete, AddressResponseType } from '@apps/registry/common/utils/smartyClient';
import { NATIONAL_COUNTRY_CODE } from './constants';
import { AddressDialogFields } from '@apps/registry/common/components/AddressDialog/types';
import { useMount } from '@shared/utils/hooks/useMount';
import { useShippingAddressTelemetry } from './ShippingAddress.telemetry';

export interface SuggestedAddress extends Readonly<{ address: string; city: string; state: string; zipCode: string }> {}

export type AddressOption = 'original' | 'suggested';

export const useAddressVerificationController = (formik: FormikProps<AddressDialogFields>) => {
  const { Lookup, handleAddressSearch } = useAddressAutocomplete();
  const [isValidatingAddress, setIsValidatingAddress] = useState<boolean>(true);
  const [addressOption, setAddressOption] = useState<AddressOption>('original');
  const [suggestedAddress, setSuggestedAddress] = useState<SuggestedAddress | undefined>(undefined);
  const telemetry = useShippingAddressTelemetry();

  const handleNationalAddressAutocomplete = useCallback(
    async (value: string) => {
      const lookup: AddressSearchType = new Lookup(value);

      lookup.includeOnlyZIPCodes = [formik.values.postalCode];

      const response = await handleAddressSearch(lookup);
      return response;
    },
    [Lookup, formik.values.postalCode, handleAddressSearch]
  );

  const fetchSuggestions = useCallback(
    async (text: string) => {
      try {
        const suggestions = await handleNationalAddressAutocomplete(text);

        if (suggestions?.length) {
          setAddressOption('suggested');
          const suggestion = suggestions[0] as AddressResponseType;
          setSuggestedAddress({
            address: suggestion.streetLine,
            city: suggestion.city,
            state: suggestion.state,
            zipCode: suggestion.zipcode
          });

          const areAddressesEqual =
            suggestion.streetLine === formik.values.address1 &&
            suggestion.city === formik.values.city &&
            suggestion.state === formik.values.state &&
            suggestion.zipcode === formik.values.postalCode;

          if (areAddressesEqual) {
            return formik.handleSubmit();
          }
        } else {
          setAddressOption('original');
          setSuggestedAddress(undefined);
        }
      } catch (error) {
        setAddressOption('original');
        setSuggestedAddress(undefined);
      }
      setIsValidatingAddress(false);
    },
    [formik, handleNationalAddressAutocomplete]
  );

  useMount(() => {
    if (formik.values.countryCode === NATIONAL_COUNTRY_CODE) {
      fetchSuggestions(formik.values.address1);
    } else {
      formik.handleSubmit();
    }
  });

  const handleOnConfirmAddressClick = () => {
    if (addressOption === 'suggested' && suggestedAddress) {
      formik.setValues({
        ...formik.values,
        address1: suggestedAddress.address,
        city: suggestedAddress.city,
        state: suggestedAddress.state,
        postalCode: suggestedAddress.zipCode,
        validated: true
      });
      telemetry.verifyAddressClicked(true);
    } else {
      formik.setValues({ ...formik.values, validated: false });
      telemetry.verifyAddressClicked(false);
    }
    formik.handleSubmit();
  };

  return {
    isValidatingAddress,
    addressOption,
    setAddressOption,
    suggestedAddress,
    handleOnConfirmAddressClick
  };
};
